import React, { useContext, useEffect } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import { IconButton, List, ListItem, ListItemSecondaryAction, ListItemText } from '@mui/material';
import { PatientTabsContext } from '../../../lib/contexts/';
import { Add, Close } from '@mui/icons-material';
import { useParams } from 'react-router';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    height: '32px',
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '1px',
      backgroundColor: theme.palette.admin.tab,
    },
  },
  activeTabs: {
    display: 'flex',
    alignItems: 'center',
    overflowX: 'auto',
    '@media print': {
      display: 'none',
    },
  },
  activeTab: {
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
    backgroundColor: theme.palette.admin.activeTab,
    borderTop: `1px solid ${theme.palette.admin.tab}`,
    borderLeft: `1px solid ${theme.palette.admin.tab}`,
    borderRight: `1px solid ${theme.palette.admin.tab}`,
    borderBottom: 'none',
    color: theme.palette.admin.main,
    position: 'relative',
    marginBottom: '-1px',
    zIndex: 1,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  nonActiveTab: {
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
    backgroundColor: theme.palette.admin.tab,
    color: 'rgba(0, 0, 0, 0.60)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  addTab: {
    cursor: 'pointer',
    height: '32px',
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
    backgroundColor: theme.palette.admin.tab,
    color: theme.palette.admin.main,
  },
}));

const PatientTabs = () => {
  const classes = useStyles();
  const theme = useTheme();

  const {
    state: { activeTab, openTabs },
    actions: { setActiveTab, closePatientTab, setLastListId, goHomeTab },
  } = useContext(PatientTabsContext);
  let { listId } = useParams();

  useEffect(() => {
    if (listId) {
      setLastListId(listId);
    }
  }, [listId]);

  return (
    <>
      {openTabs.length > 0 && (
        <div className={classes.wrapper}>
          <div className={classes.activeTabs}>
            <List
              dense
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                padding: 0,
                overflowX: 'auto',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '1px',
                  backgroundColor: theme.palette.admin.tab,
                  zIndex: 0,
                }}
              />
              {openTabs.map((item) => (
                <ListItem
                  key={'patientChip_' + item.uuid}
                  className={activeTab === item.uuid ? classes.activeTab : classes.nonActiveTab}
                  sx={{
                    cursor: 'pointer',
                    marginRight: '4px',
                    display: 'flex',
                    padding: '9px 16px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    minWidth: 'fit-content',
                    position: 'relative',
                    whiteSpace: 'nowrap',
                    height: '32px',
                    paddingRight: '8px',
                    '&:last-child': {
                      marginRight: 0,
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      item && item.lastName
                        ? `${item.lastName.toUpperCase()}${
                            item.firstName ? `, ${item.firstName[0].toUpperCase()}` : ''
                          }`
                        : ''
                    }
                    onClick={() => setActiveTab(item.uuid)}
                    sx={{
                      '& .MuiListItemText-primary': {
                        lineHeight: '24px',
                        paddingRight: '8px',
                        fontWeight: 500,
                      },
                    }}
                  />
                  {activeTab === item.uuid && (
                    <IconButton
                      size={'small'}
                      edge="start"
                      aria-label="close tab"
                      onClick={() => closePatientTab(item.uuid)}
                    >
                      <Close />
                    </IconButton>
                  )}
                </ListItem>
              ))}
              <ListItem
                className={!activeTab ? classes.activeTab : classes.addTab}
                sx={{
                  height: '32px',
                  padding: '9px 16px',
                }}
              >
                <IconButton
                  size={'small'}
                  edge="start"
                  aria-label="add tab"
                  onClick={goHomeTab}
                >
                  <Add
                    sx={{
                      width: '24px',
                      height: '24px',
                    }}
                  />
                </IconButton>
              </ListItem>
            </List>
          </div>
        </div>
      )}
    </>
  );
};

export default PatientTabs;
